import { Events, ImageQualityStatus } from '../../../enums/index.js';
import eventTarget from '../../../eventTarget.js';
import { getConfiguration } from '../../../init.js';
import { ensureFidelityIndicatorStyles } from './fidelityIndicatorStyles.js';
import { isLabelmapActorEntry } from './isLabelmapActorEntry.js';
import { dataKindOf, formatFactor, formatFactors, isInteractiveLod, isReduced, lineHasData, svgStateOf, } from './fidelityIndicatorState.js';
export { isLabelmapActorEntry };
const COLORS = {
    full: '#2e9d4a',
    loading: '#c98a00',
    reduced: '#2f6fc0',
    decimated: '#c0392b',
    none: '#666',
};
export const DONE_HOLD_MS = 3000;
const SVG_GLYPH = '<g class="fi-glyph">' +
    '<circle class="fi-disc" cx="7.5" cy="7.5" r="7.5"/>' +
    '<g class="fi-dot-wrap"><circle class="fi-dot" cx="7.5" cy="7.5" r="4.5"/></g>' +
    '<path class="fi-check" d="M4.7 7.8 L6.7 9.7 L10.3 5.7"/>' +
    '<path class="fi-dash" d="M5 7.5 H10"/>' +
    '</g>';
const ARIA_LABELS = {
    loading: 'Image fidelity: loading',
    done: 'Image fidelity: full resolution',
    lossy: 'Image fidelity: reduced or decimated',
    lod: 'Image fidelity: interactive LOD',
};
const cleanups = new WeakMap();
export function attachFidelityIndicator(viewport) {
    detachFidelityIndicator(viewport);
    if (getConfiguration().dangerouslyDisableFidelityIndicator) {
        return () => { };
    }
    if (!hasFidelityApi(viewport)) {
        return () => { };
    }
    const verbose = getConfiguration().debug?.fidelityIndicatorDebug === true;
    ensureFidelityIndicatorStyles();
    const { host, restore: restoreHost } = overlayHostOf(viewport.element);
    const views = [createSvgIndicator(host)];
    if (verbose) {
        views.push(createTextBadge(host));
    }
    const cleanup = subscribeToFidelity(viewport, views, restoreHost);
    const registered = () => {
        if (cleanups.get(viewport) === registered) {
            cleanups.delete(viewport);
        }
        cleanup();
    };
    cleanups.set(viewport, registered);
    return registered;
}
export function maybeAttachFidelityIndicator(viewport) {
    if (getConfiguration().dangerouslyDisableFidelityIndicator) {
        return;
    }
    if (!canReportFidelity(viewport)) {
        return;
    }
    attachFidelityIndicator(viewport);
}
export function detachFidelityIndicator(viewport) {
    const cleanup = cleanups.get(viewport);
    if (!cleanup) {
        return;
    }
    cleanups.delete(viewport);
    cleanup();
}
export function canReportFidelity(viewport) {
    return viewport.canReportFidelity() === true;
}
function hasFidelityApi(viewport) {
    return typeof viewport.getFidelityLines === 'function';
}
function linesOf(viewport) {
    return viewport.getFidelityLines?.() ?? [];
}
function subscribeToFidelity(viewport, views, restoreHost) {
    const { element } = viewport;
    let rafId = 0;
    let disposed = false;
    const update = () => {
        rafId = 0;
        if (disposed) {
            return;
        }
        const lines = linesOf(viewport);
        for (const view of views) {
            view.update(lines);
        }
    };
    const schedule = () => {
        if (!rafId && !disposed) {
            rafId = requestAnimationFrame(update);
        }
    };
    const onVolumeModified = () => {
        schedule();
    };
    const onFidelityChanged = () => {
        if (disposed) {
            return;
        }
        const lines = linesOf(viewport);
        for (const view of views) {
            view.update(lines);
        }
    };
    const cleanUp = () => {
        if (disposed) {
            return;
        }
        disposed = true;
        if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = 0;
        }
        element.removeEventListener(Events.IMAGE_RENDERED, schedule);
        element.removeEventListener(Events.VIEWPORT_FIDELITY_CHANGED, onFidelityChanged);
        for (const type of VOLUME_DATA_EVENTS) {
            eventTarget.removeEventListener(type, onVolumeModified);
        }
        for (const view of views) {
            view.remove();
        }
        restoreHost();
    };
    element.addEventListener(Events.IMAGE_RENDERED, schedule);
    element.addEventListener(Events.VIEWPORT_FIDELITY_CHANGED, onFidelityChanged);
    for (const type of VOLUME_DATA_EVENTS) {
        eventTarget.addEventListener(type, onVolumeModified);
    }
    schedule();
    return cleanUp;
}
const VOLUME_DATA_EVENTS = [
    Events.IMAGE_VOLUME_MODIFIED,
    Events.IMAGE_VOLUME_LOADING_COMPLETED,
];
function overlayHostOf(element) {
    let previousInline;
    if (typeof getComputedStyle !== 'undefined') {
        const computed = getComputedStyle(element).position;
        if (computed === 'static' || computed === '') {
            previousInline = element.style.position;
            element.style.position = 'relative';
        }
    }
    return {
        host: element,
        restore: () => {
            if (previousInline === undefined) {
                return;
            }
            element.style.position = previousInline;
        },
    };
}
function createSvgIndicator(host) {
    const root = document.createElement('div');
    root.className = 'fi-root';
    Object.assign(root.style, {
        position: 'absolute',
        top: '14px',
        right: '14px',
        zIndex: '1000',
        transform: 'translateZ(0)',
    });
    root.dataset.shown = 'false';
    const htmlLayer = document.createElement('div');
    htmlLayer.className = 'fi-html';
    htmlLayer.innerHTML =
        '<div class="fi-html-ring"></div><div class="fi-html-dot"></div>';
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'fidelity-indicator');
    svg.setAttribute('width', '10');
    svg.setAttribute('height', '10');
    svg.setAttribute('viewBox', '0 0 15 15');
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', 'Image fidelity');
    const details = document.createElement('pre');
    details.className = 'fi-details';
    root.append(htmlLayer, svg, details);
    host.appendChild(root);
    let currentState = 'hidden';
    let doneFadeTimer = 0;
    let hovered = false;
    let lastLines = [];
    for (const type of ['mousedown', 'pointerdown', 'wheel']) {
        root.addEventListener(type, (event) => event.stopPropagation());
    }
    root.addEventListener('mouseenter', () => {
        hovered = true;
        root.dataset.hover = 'true';
        details.textContent = fidelityHoverTextOf(lastLines);
    });
    root.addEventListener('mouseleave', () => {
        hovered = false;
        delete root.dataset.hover;
    });
    const scheduleDoneFade = () => {
        clearTimeout(doneFadeTimer);
        doneFadeTimer = window.setTimeout(() => {
            doneFadeTimer = 0;
            if (currentState === 'done') {
                root.dataset.shown = 'false';
            }
        }, DONE_HOLD_MS);
    };
    const ensureSvgGlyph = () => {
        if (!svg.querySelector('.fi-glyph')) {
            svg.innerHTML = SVG_GLYPH;
        }
    };
    const setSvgState = (next) => {
        if (next === 'hidden') {
            if (currentState === 'hidden') {
                return;
            }
            clearTimeout(doneFadeTimer);
            doneFadeTimer = 0;
            currentState = 'hidden';
            delete root.dataset.state;
            delete root.dataset.from;
            root.dataset.shown = 'false';
            svg.innerHTML = '';
            svg.setAttribute('aria-label', 'Image fidelity');
            return;
        }
        if (next === currentState) {
            return;
        }
        if (next === 'done' && currentState === 'hidden') {
            return;
        }
        const from = currentState;
        root.dataset.from = from === 'hidden' ? 'hidden' : from;
        root.dataset.state = next;
        root.dataset.shown = 'true';
        svg.setAttribute('aria-label', ARIA_LABELS[next]);
        if (next === 'done' || next === 'lossy') {
            ensureSvgGlyph();
        }
        clearTimeout(doneFadeTimer);
        doneFadeTimer = 0;
        currentState = next;
        if (next === 'done') {
            scheduleDoneFade();
        }
    };
    return {
        update: (lines) => {
            lastLines = lines;
            setSvgState(svgStateOf(lines));
            if (hovered) {
                details.textContent = fidelityHoverTextOf(lines);
            }
        },
        remove: () => {
            clearTimeout(doneFadeTimer);
            root.remove();
        },
    };
}
function summaryOf(line) {
    const { record, reductionFactors, sampleDistanceLod, imageQualityStatus } = line;
    if (!lineHasData(line)) {
        return { text: 'NO DATA', color: COLORS.none };
    }
    const dataKind = dataKindOf(line);
    if (dataKind === 'loading') {
        if (record) {
            const loaded = record.voxels > 0
                ? Math.min(99, Math.floor((100 * (record.voxels - record.missing)) / record.voxels))
                : 0;
            return {
                text: `${record.exact ? '' : '~'}${loaded}% loaded`,
                color: COLORS.loading,
            };
        }
        return { text: 'LOADING', color: COLORS.loading };
    }
    if (dataKind === 'decimated') {
        return { text: 'DECIMATED', color: COLORS.decimated };
    }
    const parts = [];
    if (dataKind === 'reduced') {
        if (isReduced(reductionFactors)) {
            parts.push(`REDUCED ${formatFactors(reductionFactors)}`);
        }
        else {
            const status = imageQualityStatus ?? record?.lowest;
            parts.push(ImageQualityStatus[status] ?? 'REDUCED');
        }
    }
    if (isInteractiveLod(sampleDistanceLod)) {
        parts.push(`LOD ${formatFactor(sampleDistanceLod)}×`);
    }
    else if (line.interactiveReduction) {
        parts.push('LOD');
    }
    if (parts.length) {
        return { text: parts.join(' · '), color: COLORS.reduced };
    }
    return { text: 'FULL', color: COLORS.full };
}
function fidelityHoverTextOf(lines) {
    if (!lines.length) {
        return 'No image data in this viewport yet';
    }
    return lines.map(detailsOf).join('\n\n');
}
const DETAIL_STATUS = {
    loading: () => 'loading',
    decimated: () => 'decimated',
    reduced: (line) => line.record
        ? `reduced ${formatFactors(line.reductionFactors)}`
        : 'reduced',
    full: () => 'full resolution',
};
function detailsOf(line) {
    const { label, record, sampleDistanceLod, imageQualityStatus } = line;
    if (!record) {
        if (imageQualityStatus !== undefined) {
            const status = ImageQualityStatus[imageQualityStatus] ?? String(imageQualityStatus);
            return [
                `${label}`,
                `  status    ${DETAIL_STATUS[dataKindOf(line)](line)}`,
                `  delivery  ${status}`,
                `  note      developer view — app indicators supply clinician text`,
            ].join('\n');
        }
        return line.fullResolution
            ? `${label}: no quality record`
            : `${label}: no quality record, reduced ${formatFactors(line.reductionFactors)}`;
    }
    const { grid } = record;
    const delivery = ImageQualityStatus[record.status] ?? String(record.status);
    const range = record.lowest === record.highest
        ? ''
        : ` [${ImageQualityStatus[record.lowest]}..${ImageQualityStatus[record.highest]}]`;
    const missingPercent = record.voxels > 0 ? Math.round((100 * record.missing) / record.voxels) : 0;
    const status = DETAIL_STATUS[dataKindOf(line)](line);
    const lodState = isInteractiveLod(sampleDistanceLod)
        ? `sample distance ×${formatFactor(sampleDistanceLod)} (interactive)`
        : line.interactiveReduction
            ? 'deferred resample (interactive)'
            : 'sample distance idle';
    return [
        `${label}`,
        `  status    ${status}`,
        `  delivery  ${delivery}${range}`,
        `  grid      ${grid.dimensions.join('×')} @ ${grid.spacing
            .map((value) => formatFactor(value))
            .join('×')} mm`,
        `  missing   ${record.exact ? '' : '~'}${missingPercent}% of ${record.voxels} voxels`,
        `  source    ${record.source} · ${record.reduction} · ${record.deliveries} deliveries`,
        `  lod       ${lodState}`,
        `  note      developer view — app indicators supply clinician text`,
    ].join('\n');
}
function createTextBadge(host) {
    const root = document.createElement('div');
    Object.assign(root.style, {
        position: 'absolute',
        top: '4px',
        left: '4px',
        zIndex: '1000',
        font: '11px/1.3 monospace',
        color: '#fff',
        textAlign: 'left',
        userSelect: 'none',
    });
    const pills = document.createElement('div');
    const details = document.createElement('pre');
    Object.assign(details.style, {
        display: 'none',
        margin: '4px 0 0',
        padding: '6px 8px',
        background: 'rgba(0, 0, 0, 0.85)',
        borderRadius: '4px',
        textAlign: 'left',
        whiteSpace: 'pre',
    });
    root.append(pills, details);
    let hovered = false;
    let lastLines = [];
    let lastPillsKey = '';
    for (const type of ['mousedown', 'pointerdown', 'wheel']) {
        root.addEventListener(type, (event) => event.stopPropagation());
    }
    root.addEventListener('mouseenter', () => {
        hovered = true;
        details.textContent = fidelityHoverTextOf(lastLines);
        details.style.display = 'block';
    });
    root.addEventListener('mouseleave', () => {
        hovered = false;
        details.style.display = 'none';
    });
    host.appendChild(root);
    return {
        update: (lines) => {
            lastLines = lines;
            renderPills(lines);
            if (hovered) {
                details.textContent = fidelityHoverTextOf(lines);
            }
        },
        remove: () => root.remove(),
    };
    function renderPills(lines) {
        const shown = lines.length
            ? lines
            : [
                {
                    label: 'Image',
                    reductionFactors: [1, 1, 1],
                    fullResolution: true,
                    sampleDistanceLod: 1,
                },
            ];
        const summaries = shown.map((line) => ({ line, ...summaryOf(line) }));
        const key = summaries.map(({ text, color }) => text + color).join('|');
        if (key === lastPillsKey) {
            return;
        }
        lastPillsKey = key;
        pills.replaceChildren(...summaries.map(({ line, text, color }) => {
            const pill = document.createElement('div');
            Object.assign(pill.style, {
                display: 'inline-block',
                marginRight: '4px',
                padding: '1px 6px',
                borderRadius: '8px',
                background: color,
            });
            pill.textContent = shown.length > 1 ? `${line.label} ${text}` : text;
            return pill;
        }));
    }
}
